import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, ChevronLeft, Loader2, Search, Mail, Phone, Globe2,
  CheckCircle2, Clock, Trophy,
} from "lucide-react";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "CONVERTED", "LOST"];
const COLORS = {
  NEW: "#F77F00", CONTACTED: "#0C4B66", QUALIFIED: "#6B21A8",
  CONVERTED: "#16A34A", LOST: "#D62828",
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [completedOnly, setCompletedOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("search", q.trim());
      if (status) params.set("status", status);
      if (completedOnly) params.set("completed_only", "true");
      const r = await api.get(`/admin/leads?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) { toast.error("Failed to load leads"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, status, completedOnly]);

  const updateLead = async (id, patch) => {
    try {
      await api.patch(`/admin/leads/${id}`, patch);
      toast.success("Updated");
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, ...patch } : x));
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <DashboardLayout>
      <div className="w-full max-w-7xl mx-auto" data-testid="admin-leads-root">
        <Link to="/app/admin" className="inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.25em] uppercase mb-4 hover:text-[#F77F00] transition text-slate-500">
          <ChevronLeft className="w-3.5 h-3.5" /> Admin home
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F77F00]">// SALES · LEADS</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl mt-1 text-[#003049]">Free Assessment Leads</h1>
            <p className="text-sm md:text-base mt-1 text-slate-500">
              Every student who started the free assessment — with IP, country, band score, and status for follow-up.
            </p>
          </div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
            {items.length} leads
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, IP…"
              data-testid="leads-search"
              className="w-full pl-10 pr-3 py-2.5 rounded-sm font-mono text-sm bg-white border border-slate-200 outline-none focus:border-[#F77F00]" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-filter-status"
            className="px-3 py-2.5 rounded-sm font-mono text-sm bg-white border border-slate-200">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm font-mono text-xs uppercase tracking-[0.2em] bg-white border border-slate-200 cursor-pointer">
            <input type="checkbox" checked={completedOnly} onChange={(e) => setCompletedOnly(e.target.checked)}
              data-testid="leads-filter-completed" />
            Completed only
          </label>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500" data-testid="leads-empty">
            <Users className="w-10 h-10 mx-auto mb-3 text-[#F77F00]" />
            <div className="font-bold text-lg text-[#003049]">No leads match this filter</div>
            <div data-testid="leads-list" className="hidden" />
          </div>
        ) : (
          <div className="space-y-2" data-testid="leads-list">
            {items.map((l) => (
              <div key={l.id} data-testid={`lead-row-${l.id}`}
                className="bg-white border border-slate-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_180px_180px] gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-[#003049] truncate flex items-center gap-2">
                    {l.full_name}
                    {l.assessment_completed && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5"><Mail className="w-3 h-3" /> {l.email}</div>
                  {l.phone && <div className="flex items-center gap-1.5 text-xs text-slate-500"><Phone className="w-3 h-3" /> {l.phone}</div>}
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  <div className="flex items-center gap-1.5"><Globe2 className="w-3 h-3" /> {l.country_name || "—"} · {l.target_exam}</div>
                  <div className="mt-1">IP: {l.ip || "—"}</div>
                  <div className="flex items-center gap-1.5 mt-1"><Clock className="w-3 h-3" /> {new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-2">
                  {l.assessment_band != null ? (
                    <div className="flex items-center gap-1.5">
                      <Trophy className="w-4 h-4 text-[#F77F00]" />
                      <div>
                        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-slate-400">BAND</div>
                        <div className="font-display font-bold text-2xl text-[#F77F00] leading-none">{Number(l.assessment_band).toFixed(1)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">// IN PROGRESS</div>
                  )}
                </div>
                <select value={l.status || "NEW"} onChange={(e) => updateLead(l.id, { status: e.target.value })}
                  data-testid={`lead-status-${l.id}`}
                  className="px-3 py-2 rounded-sm font-mono text-xs uppercase tracking-[0.2em] border-2"
                  style={{ background: "#fff", borderColor: COLORS[l.status || "NEW"], color: COLORS[l.status || "NEW"] }}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
